import React, { useCallback, useEffect, useState } from 'react'
import Loader from '../../../Components/Loader'
import TitleWithAddButton from '../../Home/TitleWithAddButton'
import ReusableDrawer from '../../../Components/ReusableDrawer'
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Paper,
  Avatar,
  Card,
  CardContent,
} from '@mui/material'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CallIcon from '@mui/icons-material/Call'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import { align_center, VM_Colors } from '../../../Styles/VMStyles'
import StorageFactory from '../../../Util/StorageFactory'
import {
  ACCESS_RESTRICTED_MESSAGE,
  ROUTE_ENQUIRY_DETAILS,
  SESSION_USER_COUNTRY_DATA_KEY,
  SESSION_USER_ID_KEY,
  SESSION_USER_ROLES_PERMISSIONS,
  STATUS_COMPLETED,
  STATUS_INPROGRESS,
  STATUS_PENDING,
  TEAM_MEMBERS_REQUIRED_MESSAGE,
} from '../../../Util/VMConstants'
import {
  API_DELETE_CHECKLIST_DATA,
  API_GET_CHECKLIST_DATA
} from '../../../Util/URLConstants'
import {
  checkIsPermissionExist,
  displayFormattedDate,
  formattedDateTime,
  hasAdminTeamMembers,
  renderSNo,
  renderWithTooltip,
  returnDate,
  updateColorBasedOnStatus,
} from '../../../Util/UtilHelper'
import { canMutateRecord, RECORD_OWNERSHIP_DENIED_MESSAGE } from '../../../Util/recordAuthorization'
import { toast } from 'react-toastify'
import axios from 'axios'
import ReplayIcon from '@mui/icons-material/Replay'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckListDrawer from './CheckListDrawer'
import CheckListHistory from './CheckListHistory'
import { checkListStatusConfig, getPriorityColor } from '../../../Util/ConstnatUtils'
import MUITypography from '../../../Components/MUITypography'
import StatusButton from '../../../Components/StatusButton'
import SubscriptionDialog from '../../../Util/SubscriptionDialog'
import MUIAlertDialog from '../../../Components/MUIAlertDialog'
import { useSelector } from 'react-redux'
import NoPermission from '../../../Components/NoPermission'
import ReusableDataGrid from '../../../Components/ReusableDataGrid'
import { useNavigate } from 'react-router-dom'

const getEnquiryInfo = (info) => {
  if (!info) return null
  if (Array.isArray(info)) return info[0] || null
  if (typeof info === 'object') return info
  return null
}

const formatEventDates = (dates) => {
  if (!dates) return ''
  const list = Array.isArray(dates) ? dates : [dates]
  return list.filter(Boolean).map((d) => returnDate(d)).join(', ')
}

const formatPhoneNumber = (countryCode, phone) => {
  if (!countryCode && !phone) return ''
  const code = countryCode ? String(countryCode).trim() : ''
  const prefix = code ? (code.startsWith('+') ? code : `+${code}`) : ''
  return [prefix, phone].filter(Boolean).join(' ')
}

const getInitials = (name) =>
  String(name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?'

const EventInfoTile = ({ icon, label, value }) => {
  if (!value) return null
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          bgcolor: 'rgba(30, 58, 138, 0.08)',
          color: VM_Colors.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: VM_Colors.steel_gray,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            display: 'block',
            lineHeight: 1.2,
            mb: 0.35,
          }}
        >
          {label}
        </Typography>
        <Typography
          component="div"
          variant="body2"
          sx={{
            fontWeight: 600,
            color: '#1A1A1A',
            wordBreak: 'break-word',
            lineHeight: 1.4,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  )
}

const EventDetailsCard = ({ enquiryInfo, enquiryId, onViewEvent }) => {
  const eventInfo = getEnquiryInfo(enquiryInfo)
  const linkedEnquiryId = eventInfo?.appointment_id || enquiryId
  const eventDates = formatEventDates(eventInfo?.appointment_date)
  const eventPhone = formatPhoneNumber(eventInfo?.country_code, eventInfo?.phone_no)
  const customerName = eventInfo?.name || 'Linked enquiry'
  const initials = getInitials(customerName)

  if (!eventInfo && !linkedEnquiryId) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2.5,
          border: '1px dashed #D7DEE8',
          bgcolor: VM_Colors.white1,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: VM_Colors.primary }}>
          Event Details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No event is linked to this checklist.
        </Typography>
      </Paper>
    )
  }

  const phoneDigits = `${String(eventInfo?.country_code || '').replace(/[^\d]/g, '')}${eventInfo?.phone_no || ''}`

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: 2.5,
        overflow: 'hidden',
        border: '1px solid #E4EAF2',
        boxShadow: '0 8px 24px rgba(30, 58, 138, 0.06)',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          background: `linear-gradient(90deg, ${VM_Colors.primary} 0%, #2B4CB3 100%)`,
          color: VM_Colors.white,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              bgcolor: 'rgba(255,255,255,0.16)',
              color: VM_Colors.white,
              fontWeight: 700,
              fontSize: 16,
              border: '1px solid rgba(255,255,255,0.25)',
            }}
          >
            {initials || 'E'}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ opacity: 0.8, letterSpacing: 0.8, textTransform: 'uppercase', fontWeight: 700 }}>
              Event Details
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25, wordBreak: 'break-word' }}>
              {customerName}
            </Typography>
            {linkedEnquiryId && (
              <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 500 }}>
                {linkedEnquiryId}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {eventInfo?.appointment_status && (
            <Chip
              label={eventInfo.appointment_status}
              size="small"
              sx={{
                height: 26,
                fontWeight: 700,
                bgcolor: 'rgba(255,255,255,0.16)',
                color: VM_Colors.white,
                border: '1px solid rgba(255,255,255,0.28)',
              }}
            />
          )}
          {/* {linkedEnquiryId && (
            <Button
              size="small"
              variant="contained"
              endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
              onClick={() => onViewEvent(linkedEnquiryId)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: VM_Colors.white,
                color: VM_Colors.primary,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#F4F7FD', boxShadow: 'none' },
              }}
            >
              View event
            </Button>
          )} */}
        </Stack>
      </Box>

      <Box
        sx={{
          p: 2.5,
          bgcolor: VM_Colors.white,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2.25,
        }}
      >
        <EventInfoTile
          icon={<ConfirmationNumberOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Enquiry ID"
          value={linkedEnquiryId}
        />
        <EventInfoTile
          icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Event date"
          value={eventDates}
        />
        <EventInfoTile
          icon={<PhoneOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Phone"
          value={
            eventPhone ? (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box component="span">{eventPhone}</Box>
                {phoneDigits && (
                  <Tooltip title="Call customer">
                    <IconButton
                      size="small"
                      component="a"
                      href={`tel:${phoneDigits}`}
                      sx={{ color: VM_Colors.primary, p: 0.25 }}
                    >
                      <CallIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            ) : null
          }
        />
        <EventInfoTile
          icon={<PlaceOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Address"
          value={eventInfo?.address}
        />
        <EventInfoTile
          icon={<MailOutlineIcon sx={{ fontSize: 18 }} />}
          label="Email"
          value={eventInfo?.email}
        />
        {eventInfo?.notes ? (
          <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
            <EventInfoTile
              icon={<NotesOutlinedIcon sx={{ fontSize: 18 }} />}
              label="Notes"
              value={eventInfo.notes}
            />
          </Box>
        ) : null}
      </Box>
    </Paper>
  )
}

const PersonSnapshot = ({ title, person }) => {
  const name = person?.name || '—'
  const phone = formatPhoneNumber(person?.country_code, person?.mobile_no)

  return (
    <Box
      sx={{
        p: 1.75,
        height: '100%',
        borderRadius: 2,
        border: '1px solid #E4EAF2',
        bgcolor: VM_Colors.white1,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: VM_Colors.steel_gray,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
        }}
      >
        {title}
      </Typography>
      {person ? (
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mt: 1.25 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: VM_Colors.primary,
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {getInitials(name)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1A1A1A' }}>
              {name}
            </Typography>
            {person.user_type && (
              <Chip
                label={person.user_type}
                size="small"
                sx={{
                  mt: 0.5,
                  height: 22,
                  fontWeight: 600,
                  bgcolor: 'rgba(30, 58, 138, 0.08)',
                  color: VM_Colors.primary,
                }}
              />
            )}
            {person.email && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {person.email}
              </Typography>
            )}
            {phone && (
              <Typography variant="caption" color="text.secondary" display="block">
                {phone}
              </Typography>
            )}
          </Box>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Not assigned
        </Typography>
      )}
    </Box>
  )
}

function VMCheckList() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [reuseData, setReuseData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [selectedCheckListDetail, setSelectedCheckListDetail] = useState(null)
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false)
  const [openSubscribeDialog, setOpenSubscribeDialog] = useState(false)
  const [teamMembersAlertOpen, setTeamMembersAlertOpen] = useState(false)
  const navigate = useNavigate();
  const [dashboardStats, setDashboardStats] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    pending: 0,
    completionRate: 0
  })

  const subscriptionStatus = useSelector(
    (state) => state.subscription.status
  );
  

  const sessionFactory = new StorageFactory('session')
  const userID = sessionFactory.get(SESSION_USER_ID_KEY)
  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS)
  const currencySymbol = sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || '₹';

  const openChecklistDrawerIfTeamExists = useCallback(
    async (onOpen) => {
      const hasTeam = await hasAdminTeamMembers(userID)
      if (hasTeam) {
        onOpen()
      } else {
        setTeamMembersAlertOpen(true)
      }
    },
    [userID]
  )

  // 🔹 Status filter states
  const filterStatuses = ['All', STATUS_PENDING, STATUS_INPROGRESS, STATUS_COMPLETED]
  const [statusFilter, setStatusFilter] = useState('All')

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [deleteId, setDeleteId] = useState(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const statusQuery = statusFilter !== 'All' ? `&status=${statusFilter}` : ''
      const url = `${API_GET_CHECKLIST_DATA}?user_id=${userID}&page=${page}&no_of_rows=${pageSize}${statusQuery}`
      const res = await fetch(url)
      const resp = await res.json()

      if (resp.status_code === 200 && Array.isArray(resp.data)) {
        const formattedData = resp.data.map((cls, idx) => ({
          raw: cls,   // 🔥 KEY FIX FOR REUSE
          s_no: idx + 1 + page * pageSize,
          id: cls.id,
          name: cls.checklist_name,
          checklist_status: cls.checklist_status,
          checklist_points: cls.checklist_points,
          priority: cls.priority,
          created_date: formattedDateTime(cls.created_date),
          due_date: cls.due_date,
          role: cls.role,
          enquiry_id: cls.enquiry_id,
          enquiry_info: cls.enquiry_info,
          assign_users: cls.assign_users,
          created_by: cls.created_by,
        }))

        setRows(formattedData)
        setTotalCount(resp.totalCount || resp.data.length || 0)

        const summary = resp?.summary || {}
        setDashboardStats({
          total: summary.total_checklists,
          completed: summary.completed_checklists,
          inProgress: summary.in_progress_checklists,
          pending: summary.pending_checklists,
          completionRate:
            summary.total_checklists > 0
              ? Math.round((summary.completed_checklists / summary.total_checklists) * 100)
              : 0
        })
      } else {
        toast.error(resp.message || 'Unexpected response from server')
        setRows([])
      }
    } catch (err) {
      console.error('Error fetching checklists:', err)
      toast.error('Failed to load checklists')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, statusFilter, userID])

  const handleFilterChange = (status) => {
    setStatusFilter(status)
    setPage(0)
  }

  const handleOpenDeleteDialog = (id) => {
    setDeleteId(id)
    setOpenDeleteDialog(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteId(null)
    setOpenDeleteDialog(false)
  }

  const handleConfirmDelete = async () => {
    if (!deleteId) return
    const record = rows.find((r) => String(r.id) === String(deleteId))
    if (record && !canMutateRecord(VM_PERMISSIONS, 'VM033', record)) {
      toast.error(RECORD_OWNERSHIP_DENIED_MESSAGE)
      handleCloseDeleteDialog()
      return
    }
    try {
      const response = await axios.get(API_DELETE_CHECKLIST_DATA, {
        params: {
          user_id: userID,
          id: deleteId
        }
      })
      const data = response.data

      if (data.status_code === 200) {
        toast.success('Checklist removed successfully')
        fetchData()
      } else {
        toast.error(data.message || 'Failed to delete checklist')
      }
    } catch (error) {
      console.error(error)
      toast.error('Error deleting checklist')
    } finally {
      handleCloseDeleteDialog()
    }
  }

  const columns = [
    renderSNo(),
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'checklist_points',
      headerName: 'Checklist Points',
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        const points = Array.isArray(params.row.checklist_points)
          ? params.row.checklist_points
          : []

        return (
          <Tooltip
            title={
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {points.map((point, index) => (
                  <li key={index}>
                    <Typography p={0.5}>{point}</Typography>
                  </li>
                ))}
              </Box>
            }
            placement="top-start"
            arrow
          >
            <Box
              component="ul"
              sx={{
                pl: 2,
                my: 0.5,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {points.map((point, index) => (
                <li key={`${point}-${index}`}>
                  <MUITypography sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {point}
                  </MUITypography>
                </li>
              ))}
            </Box>
          </Tooltip>
        )
      }
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%', width: '100%' }}>
          <Chip label={`${params.value}`} color={getPriorityColor(params.value)} variant="outlined" />
        </Box>
      )
    },
    // { field: 'created_date', headerName: 'Created Date', flex: 0.6 },
    {
      field: 'created_date',
      headerName: 'Created Date',
      flex: 0.8,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%', width: '100%' }}>
          {renderWithTooltip(displayFormattedDate(p.value, currencySymbol))}
        </Box>
      )
    },
    {
      field: 'assign_users',
      headerName: 'Assign Users',
      flex: 0.7,
      renderCell: (params) => {
        const user = params.value || {}
        return (
          <Tooltip
            title={
              <Box >
                <Typography variant="body2">Name: {user.name || ''}</Typography>
                <Typography variant="body2">Email: {user.email || ''}</Typography>
                <Typography variant="body2">
                  Phone: {user.country_code + ' ' || ''}
                  {user.mobile_no || ''}
                </Typography>
                <Typography variant="body2">Role: {user.user_type || ''}</Typography>
              </Box>
            }
            arrow
          >
            <Box
              sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                whiteSpace: 'normal',
                wordBreak: 'break-word'
              }}
            >
              <MUITypography sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {user.name || ''}
              </MUITypography>
              {/* {user.email && (
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {user.email}
                </Typography>
              )} */}
            </Box>
          </Tooltip>
        )
      }
    },
    {
      field: 'checklist_status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => <StatusButton status={params.value} />
    },
    {
      field: 'id',
      headerName: 'Details',
      width: 80,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%', width: '100%' }}>
          <span
            style={{
              color: '#1976d2',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
            onClick={() => {
              setSelectedCheckListDetail(params.row)
              setDetailDrawerOpen(true)
            }}
          >
            View
          </span>
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.7,
      sortable: false,
      renderCell: (params) => (
        <Box sx={align_center}>
          {/* ♻ Reuse */}
          <Tooltip title="Reuse Checklist">
            <IconButton
              color="primary"
              onClick={() => {
                openChecklistDrawerIfTeamExists(() => {
                  setReuseData(params.row.raw);
                  setDrawerOpen(true);
                });
              }}
            >
              <ReplayIcon />
            </IconButton>
          </Tooltip>

          {/* Delete */}
          {canMutateRecord(VM_PERMISSIONS, 'VM033', params.row) && (
            <Tooltip title="Delete Checklist">
              <IconButton
                color="error"
                onClick={() => handleOpenDeleteDialog(params.row.id)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ]

  useEffect(() => {
    window.scrollTo(0, 0)
    fetchData()
  }, [fetchData])

  // const addCheckListHandler = () => {
  //    if (isSubscribed) {
  //        setDrawerOpen(true);
  //        setOpenSubscribeDialog(false);
  //     } else {
  //        setOpenSubscribeDialog(true);
  //     }
  // }

  // const handleDrawerClose = () => {
  //   setDrawerOpen(false)
  // }

  return (
    <Box>
      <TitleWithAddButton
        title="Check List"
        buttonLabel="Create CheckList"
        onAddClick={() => {
              if (subscriptionStatus?.plan === 'premium' || subscriptionStatus?.plan === 'trial') {
                  openChecklistDrawerIfTeamExists(() => {
                    setReuseData(null);
                    setDrawerOpen(true);
                    setOpenSubscribeDialog(false);
                  });
               } else {
                  setOpenSubscribeDialog(true);
               }
        }}
        isButtonDisplay={checkIsPermissionExist(VM_PERMISSIONS, 'VM031')}
      />

      {/* Dashboard Stats */}
<Box
  sx={{
    width: '100%',
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',                 // mobile
      sm: 'repeat(2, 1fr)',      // tablet
      md: 'repeat(4, 1fr)',      // desktop
    },
    gap: 2,
  }}
>
  {checkListStatusConfig.map((status, index) => (
    <Card
      key={index}
      elevation={2}
      sx={{
        height: '100%',
        borderRadius: 2,
        borderLeft: '4px solid',
        borderColor: status.borderColor,
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 6,
        },
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        {/* LEFT CONTENT */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {status.title}
          </Typography>

          <Typography
            variant="h4"
            fontWeight="bold"
            color={status.textColor}
            sx={{ mt: 0.5 }}
          >
            {dashboardStats?.[status.statKey] ?? 0}
          </Typography>

          {/* {status.subtitle && (
            <Typography variant="caption" color="text.secondary">
              {status.subtitle}
            </Typography>
          )} */}
        </Box>

        {/* RIGHT ICON */}
        <Box
          sx={{
            width: { xs: 36, md: 40 },
            height: { xs: 36, md: 40 },
            borderRadius: 2,
            background: (theme) =>
              `linear-gradient(135deg, ${
                theme.palette[status.gradientFrom?.split('.')?.[0]]?.light ||
                status.gradientFrom
              }, transparent)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={status.icon}
            alt={status.title}
            sx={{
              width: 22,
              height: 22,
              objectFit: 'contain',
            }}
          />
        </Box>
      </CardContent>
    </Card>
  ))}
</Box>


      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 2
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {filterStatuses.map((status) => (
            <Chip
              key={status}
              label={status}
              color={status === statusFilter ? 'primary' : 'default'}
              onClick={() => handleFilterChange(status)}
              clickable
              sx={{
                backgroundColor: updateColorBasedOnStatus(status),
                  // status === statusFilter ? VM_Colors.secondary : VM_Colors.primary,
                color: VM_Colors.white,
                fontWeight: status === statusFilter ? 'bold' : 'normal',
                border: status === statusFilter ? '2px solid white' : 'none',
                '&:hover': {
                  backgroundColor: updateColorBasedOnStatus(status),
                    // status === statusFilter
                    //   ? VM_Colors.secondary
                    //   : VM_Colors.primary
                }
              }}
            />
          ))}
        </Box>

        <Box sx={{ ml: 'auto', paddingRight: '10px' }}>
          <MUITypography>Count: {totalCount}</MUITypography>
        </Box>
      </Box>

      {/* Data Grid */}
      {checkIsPermissionExist(VM_PERMISSIONS, 'VM032') ? (
        <Box mt={3} sx={{ width: '100%', backgroundColor: VM_Colors.white }}>
          <ReusableDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            page={page}
            pageSize={pageSize}
            rowCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[5, 10, 20, 50]}
            paginationMode="server"
            autoHeight
            variableRowHeight
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            disableColumnMenu
            hideFooterSelectedRowCount
          />
        </Box>
      )
    :  <Box sx={{ mt: 4 }}>
            <NoPermission message={ACCESS_RESTRICTED_MESSAGE} />  
        </Box>}

      {/* Drawer */}
      <ReusableDrawer
        open={drawerOpen}
        title="Create Checklist"
        onClose={() => {
          setDrawerOpen(false);
          setReuseData(null);
        }}
        showFooter={false}
        loading={loading}
      >
        <Box sx={{ p: 2 }}>
          <Stack spacing={2}>
            <CheckListDrawer
              isOpen={drawerOpen}
              mode={reuseData ? "reuse" : "create"}
              initialData={reuseData}
              onClose={() => {
                setDrawerOpen(false);
                setReuseData(null);
              }}
              onCreateTask={fetchData}
            />
          </Stack>
        </Box>
      </ReusableDrawer>

      {/* <Loader loading={loading} /> */}

      {/* Details Drawer */}
      {selectedCheckListDetail && detailDrawerOpen && (
        <ReusableDrawer
          open={detailDrawerOpen}
          title="CheckList Details"
          onClose={() => {
            setDetailDrawerOpen(false)
            setSelectedCheckListDetail(null)
          }}
          showFooter={false}
          loading={loading}
        >
          <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Paper
              elevation={0}
              sx={{
                mb: 2.5,
                borderRadius: 2.5,
                overflow: 'hidden',
                border: '1px solid #E4EAF2',
                boxShadow: '0 8px 24px rgba(30, 58, 138, 0.06)',
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  background: `linear-gradient(90deg, ${VM_Colors.primary} 0%, #2B4CB3 100%)`,
                  color: VM_Colors.white,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, wordBreak: 'break-word' }}>
                    {selectedCheckListDetail.name || 'Untitled checklist'}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    #{selectedCheckListDetail.id}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flexShrink: 0,
                    mt: 0.25,
                    '& > div': { width: 'auto', height: 'auto' },
                  }}
                >
                  <StatusButton status={selectedCheckListDetail.checklist_status} justify="left" />
                </Box>
              </Box>

              <Box sx={{ p: 2.5, bgcolor: VM_Colors.white }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2.25,
                    mb: 2.5,
                  }}
                >
                  <EventInfoTile
                    icon={<CategoryOutlinedIcon sx={{ fontSize: 18 }} />}
                    label="Category"
                    value={selectedCheckListDetail.role}
                  />
                  <EventInfoTile
                    icon={<FlagOutlinedIcon sx={{ fontSize: 18 }} />}
                    label="Priority"
                    value={
                      <Chip
                        label={selectedCheckListDetail.priority || '—'}
                        color={getPriorityColor(selectedCheckListDetail.priority)}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 700, height: 24 }}
                      />
                    }
                  />
                  <EventInfoTile
                    icon={<EventAvailableOutlinedIcon sx={{ fontSize: 18 }} />}
                    label="Due date"
                    value={
                      selectedCheckListDetail.due_date
                        ? displayFormattedDate(selectedCheckListDetail.due_date, currencySymbol)
                        : '—'
                    }
                  />
                  <EventInfoTile
                    icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />}
                    label="Created date"
                    value={
                      displayFormattedDate(selectedCheckListDetail.created_date, currencySymbol) || '—'
                    }
                  />
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1.5,
                    mb: 2.5,
                  }}
                >
                  <PersonSnapshot title="Assigned to" person={selectedCheckListDetail.assign_users} />
                  <PersonSnapshot title="Created by" person={selectedCheckListDetail.created_by} />
                </Box>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #E4EAF2',
                    bgcolor: VM_Colors.white1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(30, 58, 138, 0.08)',
                        color: VM_Colors.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ChecklistOutlinedIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: VM_Colors.primary }}>
                        Checklist points
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Array.isArray(selectedCheckListDetail.checklist_points)
                          ? `${selectedCheckListDetail.checklist_points.length} item${
                              selectedCheckListDetail.checklist_points.length === 1 ? '' : 's'
                            }`
                          : '0 items'}
                      </Typography>
                    </Box>
                  </Stack>
                  {Array.isArray(selectedCheckListDetail.checklist_points) &&
                  selectedCheckListDetail.checklist_points.length > 0 ? (
                    <Stack spacing={1}>
                      {selectedCheckListDetail.checklist_points.map((point, idx) => (
                        <Stack
                          key={`${point}-${idx}`}
                          direction="row"
                          spacing={1.25}
                          alignItems="flex-start"
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            bgcolor: VM_Colors.white,
                            border: '1px solid #EEF2F7',
                          }}
                        >
                          <Box
                            sx={{
                              minWidth: 24,
                              height: 24,
                              borderRadius: '50%',
                              bgcolor: VM_Colors.primary,
                              color: VM_Colors.white,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 12,
                              fontWeight: 700,
                              mt: 0.1,
                            }}
                          >
                            {idx + 1}
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, pt: 0.2 }}>
                            {point}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No checklist points available.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>

            <EventDetailsCard
              enquiryInfo={selectedCheckListDetail.enquiry_info}
              enquiryId={selectedCheckListDetail.enquiry_id}
              onViewEvent={(enquiryId) =>
                navigate('/' + ROUTE_ENQUIRY_DETAILS, {
                  state: { enquiry_id: enquiryId },
                })
              }
            />

            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2.5,
                border: '1px solid #E4EAF2',
                boxShadow: '0 8px 24px rgba(30, 58, 138, 0.06)',
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(30, 58, 138, 0.08)',
                    color: VM_Colors.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <HistoryOutlinedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: VM_Colors.primary, lineHeight: 1.2 }}>
                    Activity history
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Updates, comments, and point progress
                  </Typography>
                </Box>
              </Stack>
              <CheckListHistory id={selectedCheckListDetail.id} />
            </Paper>
          </Box>
        </ReusableDrawer>
      )}


      <SubscriptionDialog
        open={openSubscribeDialog}
        onClose={() => setOpenSubscribeDialog(false)}
        onSubscribe={() => {
          setOpenSubscribeDialog(false);
          window.location.href = '/subscription';
        }}
      />

      <MUIAlertDialog
        open={teamMembersAlertOpen}
        onClose={() => setTeamMembersAlertOpen(false)}
        title="Team members required"
        message={TEAM_MEMBERS_REQUIRED_MESSAGE}
        okClickHandler={() => {setTeamMembersAlertOpen(false) 
          navigate('/team');
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this checklist? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default VMCheckList
